import { useEffect, useRef, useState } from "react";
import { Building2, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Company switcher (e.g. MADIO Gruppo ⇄ Lucente Kitchens). Shown only to people whose accounts in two companies the
 * platform owner has linked. Switching signs in to the linked account (POST /auth/switch/{id}) and reloads the app,
 * so its data, divisions, quotation formats and colours are that company's.
 */
export default function OrgSwitcher() {
  const { user, tenant } = useAuth();
  const [linked, setLinked] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get("/auth/linked-accounts", { skipCache: true }).then(({ data }) => setLinked(data || [])).catch(() => setLinked([]));
  }, [user]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  if (!linked.length) return null;
  const here = tenant?.display_name || tenant?.name || "This company";

  const go = async (acct) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/switch/${acct.user_id}`);
      localStorage.setItem("crm_token", data.token);
      localStorage.setItem("crm_user", JSON.stringify(data.user));
      window.location.assign("/");                   // a fresh start: nothing of the other company stays on screen
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't switch company");
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={box}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-haspopup="menu" disabled={busy}
              className="flex items-center gap-1.5 max-w-[8.5rem] sm:max-w-[16rem] px-2 sm:px-2.5 py-1.5 rounded-lg border border-[var(--color-border,#e5e7eb)] bg-white text-sm font-semibold hover:bg-black/[0.03]"
              data-testid="org-switcher">
        <Building2 size={15} className="shrink-0" style={{ color: tenant?.primary_color || undefined }} />
        <span className="truncate">{busy ? "Switching…" : here}</span>
        <ChevronDown size={14} className="shrink-0" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 sm:left-0 sm:right-auto mt-2 w-64 bg-white border border-[var(--color-border,#e5e7eb)] rounded-xl shadow-xl z-50 py-1">
          <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-[var(--color-text-muted,#6b7280)]">Company</div>
          <div className="px-3 py-2 flex items-center gap-2 text-sm">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: tenant?.primary_color || "#0062D2" }} />
            <span className="flex-1 truncate font-medium">{here}</span><Check size={14} />
          </div>
          {linked.map((a) => (
            <button key={a.user_id} type="button" role="menuitem" onClick={() => go(a)}
                    className="w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-black/[0.04]" data-testid={`org-switch-${a.tenant_id}`}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: a.color || "#888" }} />
              <span className="flex-1 truncate">{a.company}</span>
              <span className="text-[11px] text-[var(--color-text-muted,#6b7280)] font-mono">{a.username}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
